
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertCircle, AlertOctagon, Trophy, ThumbsDown } from "lucide-react";
import { ProgressBar } from './ProgressBar';

interface StatsCardProps {
  title: string;
  stats: {
    threshold: number;
    overPercentage: string;
  }[];
  color?: string;
  customLabels?: string[];
  total?: number;
  average?: string;
  most?: { team: string; value: number };
  least?: { team: string; value: number };
}

const StatsCard: React.FC<StatsCardProps> = ({
  title,
  stats,
  color = "bg-blue-500",
  customLabels,
  total,
  average,
  most,
  least
}) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-semibold">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {(total || average || most || least) && (
            <div className="space-y-2">
              {total !== undefined && (
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-yellow-500" />
                  <span>Total: {total}</span>
                </div>
              )}
              {average !== undefined && (
                <div className="flex items-center gap-2">
                  <AlertOctagon className="h-4 w-4 text-red-500" />
                  <span>Average: {average}</span>
                </div>
              )}
              {most && (
                <div className="flex items-center gap-2 mt-2">
                  <Trophy className="h-4 w-4 text-yellow-500" />
                  <span>Most: {most.team} ({most.value.toFixed(2)})</span>
                </div>
              )}
              {least && (
                <div className="flex items-center gap-2">
                  <ThumbsDown className="h-4 w-4 text-blue-500" />
                  <span>Least: {least.team} ({least.value.toFixed(2)})</span>
                </div>
              )}
            </div>
          )}
          
          <div className="space-y-2">
            {stats.map(({ threshold, overPercentage }, index) => (
              <div key={threshold} className="flex items-center gap-4">
                <span className="text-sm w-24">
                  {customLabels ? customLabels[index] : `Over ${threshold.toFixed(1)}`}
                </span>
                <div className="flex-1">
                  <ProgressBar percentage={overPercentage} color={color} />
                </div>
                <span className="text-sm font-medium w-16 text-right">{overPercentage}%</span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default StatsCard;
